import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { duelService } from '../../../services/socialService';
import ShareableWinnerScreen from './ShareableWinnerScreen';
import { duelActivityService } from '../../../services/socialService';

const DuelResultsModal = ({ isOpen, onClose, duel, userId, onRematch, onChallengeAgain }) => {
  const [loading, setLoading] = useState(false);
  const [reactionSent, setReactionSent] = useState(null);
  const [showShareScreen, setShowShareScreen] = useState(false);
  const [userStats, setUserStats] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserStats();
    }
  }, [isOpen, userId]);

  const loadUserStats = async () => {
    try {
      const stats = await duelActivityService?.getUserDuelStats(userId);
      setUserStats(stats);
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  if (!isOpen || !duel) return null;

  const isCreator = duel?.creator_id === userId;
  const opponent = isCreator ? duel?.opponent : duel?.creator;
  const isWinner = duel?.winner_id === userId;
  const isLoser = duel?.winner_id && duel?.winner_id !== userId;
  const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
  const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;
  
  // Define userProfile based on creator/opponent logic
  const userProfile = isCreator ? duel?.creator : duel?.opponent;

  const handleRematch = async () => {
    setLoading(true);
    try {
      // Create new duel with same parameters
      const newDuel = {
        opponent_id: opponent?.id,
        title: `Revancha: ${duel?.title}`,
        description: duel?.description,
        metric: duel?.metric,
        target_value: duel?.target_value,
        duration_days: duel?.duration_days,
        stake_id: duel?.stake_id,
        verification_mode: duel?.verification_mode
      };
      await duelService?.createDuel(newDuel, userId);
      onRematch?.();
      onClose();
    } catch (error) {
      console.error('Error creating rematch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChallengeAgain = () => {
    onChallengeAgain?.();
    onClose();
  };

  const handleReaction = (reactionType) => {
    setReactionSent(reactionType);
    // In a real app, this would send the reaction to the backend
    setTimeout(() => setReactionSent(null), 2000);
  };

  const metricIcons = {
    gym_workouts: 'Dumbbell',
    calories_limit: 'Flame',
    sleep_hours: 'Moon',
    water_intake: 'Droplets',
    daily_steps: 'Footprints',
    daily_minimum: 'CheckCircle'
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-3xl bg-card rounded-xl shadow-wellness-lg elevation-3 max-h-[90vh] overflow-hidden"
      >
        {/* Header with close button */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <h2 className="text-2xl font-bold text-foreground">Resultados del Duelo</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Winner/Loser Announcement */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`p-8 rounded-2xl mb-6 text-center ${
              isWinner 
                ? 'bg-gradient-to-br from-success/20 to-success/5 border-2 border-success/30' :'bg-gradient-to-br from-error/20 to-error/5 border-2 border-error/30'
            }`}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
              className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center ${
                isWinner ? 'bg-success/20' : 'bg-error/20'
              }`}
            >
              <Icon 
                name={isWinner ? 'Trophy' : 'X'} 
                size={48} 
                className={isWinner ? 'text-success' : 'text-error'} 
              />
            </motion.div>
            <h3 className={`text-3xl font-bold mb-2 ${
              isWinner ? 'text-success' : 'text-error'
            }`}>
              {isWinner ? '¡Ganaste!' : 'Perdiste'}
            </h3>
            <p className="text-lg text-muted-foreground">
              {isWinner 
                ? `¡Felicidades! Superaste a ${opponent?.full_name}` 
                : `${opponent?.full_name} te superó esta vez`
              }
            </p>
          </motion.div>

          {/* Duel Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-muted/30 rounded-xl p-6 mb-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name={metricIcons?.[duel?.metric] || 'Target'} size={24} className="text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground">{duel?.title}</h4>
                <p className="text-sm text-muted-foreground">{duel?.description}</p>
              </div>
            </div>

            {/* Final Scores */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-xl border-2 ${
                isWinner 
                  ? 'bg-success/5 border-success/30' :'bg-muted/50 border-border'
              }`}>
                <div className="flex items-center space-x-3 mb-2">
                  {userProfile?.avatar_url ? (
                    <img src={userProfile?.avatar_url} alt={userProfile?.full_name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {userProfile?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-foreground">Tú</p>
                    <p className="text-xs text-muted-foreground">{isWinner ? 'Ganador' : 'Perdedor'}</p>
                  </div>
                </div>
                <div className="text-center mt-3">
                  <p className="text-3xl font-bold text-foreground">{userProgress}</p>
                  <p className="text-xs text-muted-foreground">de {duel?.target_value}</p>
                </div>
              </div>

              <div className={`p-4 rounded-xl border-2 ${
                !isWinner 
                  ? 'bg-success/5 border-success/30' :'bg-muted/50 border-border'
              }`}>
                <div className="flex items-center space-x-3 mb-2">
                  {opponent?.avatar_url ? (
                    <img src={opponent?.avatar_url} alt={opponent?.full_name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <span className="text-sm font-semibold text-muted-foreground">
                        {opponent?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-foreground">{opponent?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{!isWinner ? 'Ganador' : 'Perdedor'}</p>
                  </div>
                </div>
                <div className="text-center mt-3">
                  <p className="text-3xl font-bold text-foreground">{opponentProgress}</p>
                  <p className="text-xs text-muted-foreground">de {duel?.target_value}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stake Assignment */}
          {duel?.stake && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`p-6 rounded-xl mb-6 border-2 ${
                isLoser 
                  ? 'bg-warning/10 border-warning/30' :'bg-success/5 border-success/20'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isLoser ? 'bg-warning/20' : 'bg-success/20'
                }`}>
                  <Icon name={duel?.stake?.icon || 'AlertCircle'} size={24} className={isLoser ? 'text-warning' : 'text-success'} />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-foreground mb-1">
                    {isLoser ? 'Debes cumplir el stake' : 'Ganaste el stake'}
                  </h4>
                  <p className="text-sm font-medium text-foreground mb-2">{duel?.stake?.title}</p>
                  <p className="text-sm text-muted-foreground">{duel?.stake?.description}</p>
                  {isLoser && (
                    <div className="mt-3 p-3 bg-warning/10 rounded-lg">
                      <p className="text-xs text-warning font-medium">⚠️ Recuerda cumplir tu palabra y completar el stake</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Social Reactions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-muted/30 rounded-xl p-6 mb-6"
          >
            <h4 className="text-sm font-semibold text-foreground mb-4">Reacciones</h4>
            <div className="flex items-center justify-center space-x-4">
              <Button
                variant={reactionSent === 'celebrate' ? 'success' : 'outline'}
                size="lg"
                onClick={() => handleReaction('celebrate')}
                className="flex-1"
              >
                <Icon name="ThumbsUp" size={20} className="mr-2" />
                {isWinner ? 'Celebrar' : 'Felicitar'}
              </Button>
              <Button
                variant={reactionSent === 'commiserate' ? 'warning' : 'outline'}
                size="lg"
                onClick={() => handleReaction('commiserate')}
                className="flex-1"
              >
                <Icon name="Heart" size={20} className="mr-2" />
                {isWinner ? 'Animar' : 'Consuelo'}
              </Button>
              <Button
                variant={reactionSent === 'share' ? 'default' : 'outline'}
                size="lg"
                onClick={() => handleReaction('share')}
                className="flex-1"
              >
                <Icon name="Share2" size={20} className="mr-2" />
                Compartir
              </Button>
            </div>
            {reactionSent && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-sm text-success mt-3"
              >
                ✓ Reacción enviada
              </motion.p>
            )}
          </motion.div>

          {/* W/L Record */}
          {userStats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-muted/30 rounded-xl p-6 mb-6"
            >
              <h4 className="text-sm font-semibold text-foreground mb-4 text-center">Tu Historial de Duelos</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-success/10 rounded-lg border border-success/30">
                  <p className="text-2xl font-bold text-success">{userStats?.duels_won || 0}</p>
                  <p className="text-xs text-muted-foreground">Victorias</p>
                </div>
                <div className="text-center p-3 bg-error/10 rounded-lg border border-error/30">
                  <p className="text-2xl font-bold text-error">{userStats?.duels_lost || 0}</p>
                  <p className="text-xs text-muted-foreground">Derrotas</p>
                </div>
                <div className="text-center p-3 bg-accent/10 rounded-lg border border-accent/30">
                  <p className="text-2xl font-bold text-accent">{userStats?.duels_completed || 0}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="space-y-3"
          >
            {/* Share Winner Screen (only for winners) */}
            {isWinner && (
              <Button
                onClick={() => setShowShareScreen(true)}
                fullWidth
                size="lg"
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
              >
                <Icon name="Share2" size={20} className="mr-2" />
                Compartir Victoria
              </Button>
            )}

            {/* Instant Rematch */}
            <Button
              onClick={handleRematch}
              loading={loading}
              disabled={loading}
              fullWidth
              size="lg"
              variant="outline"
            >
              <Icon name="RotateCcw" size={20} className="mr-2" />
              Revancha Instantánea
            </Button>

            {/* Double or Nothing */}
            <Button
              onClick={handleChallengeAgain}
              variant="outline"
              fullWidth
            >
              <Icon name="Zap" size={16} className="mr-2" />
              Doble o Nada
            </Button>
          </motion.div>

          {/* Suggested Next Challenge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20"
          >
            <div className="flex items-start space-x-3">
              <Icon name="Lightbulb" size={20} className="text-primary mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Sugerencia de Próximo Reto</p>
                <p className="text-xs text-muted-foreground">
                  {isWinner 
                    ? '¿Qué tal un reto más difícil? Aumenta la meta o reduce el tiempo.' 
                    : '¿Quieres mejorar? Intenta el mismo reto con más tiempo de preparación.'
                  }
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Shareable Winner Screen */}
      {showShareScreen && isWinner && (
        <ShareableWinnerScreen
          isOpen={showShareScreen}
          onClose={() => setShowShareScreen(false)}
          duel={duel}
          userId={userId}
          userStats={userStats}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DuelResultsModal;